import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import HandwritingCanvas from './components/HandwritingCanvas'
import './App.css'

// Base 62 characters (0-9, a-z, A-Z) — the alphabet used to slice per-session letters.
const BASE_CHARACTERS = [
  ...'0123456789'.split(''),
  ...'abcdefghijklmnopqrstuvwxyz'.split(''),
  ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')
]
const ALPHABET_SIZE = BASE_CHARACTERS.length // 62

// Each letter is rendered DIFFICULTY_LEVELS times per session (difficulty 0..9).
const DIFFICULTY_LEVELS = 10

// Build the ordered letter list for a given session index and required_score.
// Even session_index → normal (Hershey) font, slice = N / 2.
// Odd session_index → handwriting (HLHOCTRO) font, slice = (N - 1) / 2.
// Letters are BASE_CHARACTERS wrapped: [slice*R, slice*R + R - 1] mod 62.
function buildSessionPlan(sessionIndex, requiredScore) {
  const useHandwritingFont = sessionIndex % 2 === 1
  const fontId = useHandwritingFont ? 'hlhoctro' : 'current'
  const fontName = useHandwritingFont ? 'HLHOCTRO' : 'Hershey Sans 1-stroke'
  const sliceIndex = Math.floor(sessionIndex / 2)
  const letterStart = sliceIndex * requiredScore
  const letters = Array.from({ length: requiredScore }, (_, i) => {
    return BASE_CHARACTERS[(letterStart + i) % ALPHABET_SIZE]
  })
  return { fontId, fontName, letters }
}

// Given (completed, requiredScore), decode which letter + difficulty is up next.
function decodePair(completed, letters) {
  const letterIdx = Math.floor(completed / DIFFICULTY_LEVELS)
  const difficulty = completed % DIFFICULTY_LEVELS
  const char = letters[letterIdx]
  return { letterIdx, difficulty, char }
}

export default function App({
  railsEnv,
  lastProgress,
  sessionIndex = 0,
  requiredScore = 3,
  onComplete,
  onProgressSave,
  debugging = false
}) {
  const totalPairs = requiredScore * DIFFICULTY_LEVELS
  const initialCompleted = Math.min(
    parseInt(lastProgress?.completed ?? 0, 10) || 0,
    totalPairs
  )
  const [completedCount, setCompletedCount] = useState(initialCompleted)
  const [fonts, setFonts] = useState({}) // { fontId: { char: strokes } }
  const [fontsLoading, setFontsLoading] = useState(true)
  const [checkpointInfo, setCheckpointInfo] = useState({ cleared: 0, total: 17 })

  const [resetKey, setResetKey] = useState(0)
  const canvasRef = useRef(null)

  const plan = useMemo(
    () => buildSessionPlan(sessionIndex, requiredScore),
    [sessionIndex, requiredScore]
  )

  // Clamp the current pair to the last letter/difficulty if we've already
  // finished (avoids reading past the end of `letters` before onComplete fires).
  const currentIndex = Math.min(completedCount, totalPairs - 1)
  const { letterIdx, difficulty, char: selectedChar } = decodePair(currentIndex, plan.letters)
  const selectedFont = plan.fontId
  const selectedFontName = plan.fontName

  // Load fonts from static JSON files
  useEffect(() => {
    async function loadFonts() {
      try {
        const [currentFont, hlhoctroFont] = await Promise.all([
          fetch('/handwriting-fonts/current.json').then(r => r.json()),
          fetch('/handwriting-fonts/hlhoctro.json').then(r => r.json())
        ])

        setFonts({
          current: currentFont.strokes || currentFont,
          hlhoctro: hlhoctroFont.strokes || hlhoctroFont
        })
        setFontsLoading(false)
      } catch (error) {
        console.error('Failed to load fonts:', error)
        setFontsLoading(false)
      }
    }

    loadFonts()
  }, [])

  const handleSuccess = (success) => {
    if (!success) return

    const newCount = completedCount + 1
    setCompletedCount(newCount)

    if (newCount >= totalPairs) {
      if (onComplete) {
        onComplete({ completed: newCount, total: totalPairs })
      }
    } else if (onProgressSave) {
      onProgressSave({ completed: newCount, total: totalPairs })
    }
  }

  // Test helper for Cucumber tests (test environment only)
  if (typeof window !== 'undefined' && railsEnv === 'test') {
    window.__HANDWRITING_TEST_HELPER__ = {
      completeCurrentCharacter: () => {
        handleSuccess(true)
      }
    }
  }

  const handleReset = () => {
    setResetKey(prev => prev + 1)
  }

  const handleFakeCompletions = () => {
    const step = Math.min(10, totalPairs - completedCount)
    if (step <= 0) return
    const newCount = completedCount + step
    setCompletedCount(newCount)

    if (newCount >= totalPairs && onComplete) {
      onComplete({ completed: newCount, total: totalPairs })
    } else if (onProgressSave) {
      onProgressSave({ completed: newCount, total: totalPairs })
    }
  }

  // Stable identity + bail-out when values are unchanged. Without this, the
  // canvas's useEffect re-fires on every render and produces a fresh
  // `{ cleared, total }` object, triggering an infinite render loop.
  const handleCheckpointUpdate = useCallback((cleared, total) => {
    setCheckpointInfo((prev) =>
      prev.cleared === cleared && prev.total === total ? prev : { cleared, total }
    )
  }, [])

  const handleDiagnose = () => {
    if (canvasRef.current?.diagnose) {
      const info = canvasRef.current.diagnose()
      alert(`Diagnose sent to server!\n\nCharacter: ${info.character}\nDifficulty: ${difficulty}\nStroke: ${info.currentStroke + 1}/${info.totalStrokes}\nCheckpoints cleared: ${info.clearedCheckpoints.length}/${info.totalCheckpoints}\nUser path points: ${info.userPathLength}\nCanvas: ${info.canvas?.width}x${info.canvas?.height}\nRendered: ${info.boundingRect?.width?.toFixed(0)}x${info.boundingRect?.height?.toFixed(0)}`)
    }
  }

  const progressPercentage = totalPairs > 0 ? (completedCount / totalPairs) * 100 : 0

  const customStrokes = fonts[selectedFont]?.[selectedChar]

  if (fontsLoading) {
    return (
      <div className="app-container">
        <div className="app-card">
          <p style={{ textAlign: 'center', padding: '20px' }}>Loading fonts...</p>
        </div>
      </div>
    )
  }

  const roundNumber = difficulty + 1
  const letterNumber = letterIdx + 1

  return (
    <div className="app-container">
      <div className="app-card">
        <header className="app-header">
          <h1 className="app-title">Handwriting Practice</h1>
          <p className="app-subtitle">
            Letter {letterNumber} of {requiredScore} — Round {roundNumber}/{DIFFICULTY_LEVELS} (difficulty {difficulty})
          </p>
          <p className="app-subtitle" style={{ fontSize: '14px', marginTop: '4px', color: '#666' }}>
            Font: {selectedFontName}
          </p>
        </header>

        <section className="progress-section">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progressPercentage}%` }}></div>
          </div>
          <p className="checkpoint-text">Checkpoints: {checkpointInfo.cleared}/{checkpointInfo.total}</p>
        </section>

        <main className="canvas-section">
          <div className="character-display">
            <p className="character-large">{selectedChar}</p>
          </div>
          <HandwritingCanvas
            ref={canvasRef}
            key={`${resetKey}-${selectedFont}-${selectedChar}-${difficulty}`}
            character={selectedChar}
            customStrokes={customStrokes}
            difficulty={difficulty}
            onSuccess={handleSuccess}
            onCheckpointUpdate={handleCheckpointUpdate}
          />
        </main>

        <footer className="app-footer">
          <div className="footer-buttons">
            <button
              onClick={handleReset}
              className="reset-button"
              aria-label="Reset"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2.5 10C2.5 14.1421 5.85786 17.5 10 17.5C14.1421 17.5 17.5 14.1421 17.5 10C17.5 5.85786 14.1421 2.5 10 2.5C7.5 2.5 5.35714 3.75 4 5.625" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M4 2.5V5.625H7.125" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            {debugging && (
              <button
                onClick={handleDiagnose}
                className="diagnose-button"
                aria-label="Diagnose"
              >
                Diagnose
              </button>
            )}
            {(railsEnv === 'development' || railsEnv === 'test') && (
              <button
                onClick={handleFakeCompletions}
                className="fake-completion-button"
              >
                [Fake 10 Completions]
              </button>
            )}
          </div>
          {(railsEnv === 'development' || railsEnv === 'test') && (
            <p className="dev-note">Dev/Test Only - Simulates completing 10 letter-difficulty rounds</p>
          )}
        </footer>
      </div>
    </div>
  )
}
